import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../app/hooks";
import { Role } from "../features/Auth/roles";
import {
  selectCurrentUser,
  selectUserRoles
} from "../features/Users/usersSlice";
import { useEffect } from "react";
import { DeepLinkValues } from "../app/constants";
import { hasActiveTraineeOrAttendingMembership } from "../app/utils";

export const RootRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkValue = searchParams.get("deep_link_value");

  const roles = useAppSelector(selectUserRoles);
  const currentUser = useAppSelector(selectCurrentUser);

  // "Can this person see the Assessments flow?" — mirrors the mobile
  // clients' `isTrainee() || isAttending()` predicate. See
  // `app/utils.ts` and `features/nav/Layout.tsx`.
  const canAssess = hasActiveTraineeOrAttendingMembership(currentUser);
  const isProgramAdmin = roles?.includes(Role.ProgramAdmin);
  const isSysAdmin = roles?.includes(Role.SystemAdmin);

  useEffect(() => {
    // Handle deep_link_value from AppsFlyer OneLink redirects (assessment emails)
    if (
      deepLinkValue === DeepLinkValues.ASSESSMENT_REQUESTED ||
      deepLinkValue === DeepLinkValues.ASSESSMENT_COMPLETED
    ) {
      // Assessment links should go to pending assessments
      // but only if user has access to that route
      if (canAssess) {
        navigate("/assessments/pending");
        return;
      }
      // If admin doesn't have assessment access, fall through to permission-based routing
    }

    // Default permission-based routing (when no deep_link_value or user lacks access).
    // SysAdmins land on the admin area.
    if (isSysAdmin) {
      navigate("/users");
      return;
    }
    // Assessors (Trainee/Attending) land on their assessment queue — even if they
    // are ALSO a Program Admin (e.g. an attending who administers the program).
    // Previously the admin check ran first, so PA+Attending landed on /users.
    if (canAssess) {
      navigate("/assessments/pending");
      return;
    }
    // Non-assessing Program Admins land on Users.
    if (isProgramAdmin) {
      navigate("/users");
      return;
    }
  }, [deepLinkValue, isSysAdmin, isProgramAdmin, canAssess, navigate]);

  return null;
};
